import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { ApiDataSource } from 'src/app/utils/api-data-source';
import { BackgroundExportService } from 'src/app/shared/background-export.service';
import { BackgroundExportsFilterComponent } from '../background-exports-filter/background-exports-filter.component';

/**
 * Shape of a single row returned by `GET v2/background-exports`.
 */
export interface BackgroundExportRow {
  id: number;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  feature: string;
  format: string;
  file_name: string | null;
  file_mime: string | null;
  error_message: string | null;
  started_at: number | null;
  completed_at: number | null;
  date_created: number | null;
  date_modified: number | null;
  file_url: string | null;
}

/**
 * "My Exports" list page: shows the authenticated user's recent background
 * export records (CSV, XLSX, PDF, or any future format across any feature)
 * with a one-click download for completed rows and a human-readable failure
 * summary for failed rows.
 */
@Component({
  standalone: false,
  selector: 'app-background-exports',
  templateUrl: './background-exports.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./background-exports.component.scss']
})
export class BackgroundExportsComponent implements OnInit, OnDestroy {
  listColumns: string[] = [
    'feature',
    'format',
    'status',
    'file_name',
    'date_created',
    'actions'
  ];
  listDataSource: BackgroundExportsDataSource;

  // Paginators and sorter.
  @ViewChild('paginator1') paginator1!: MatPaginator;
  @ViewChild('paginator2') paginator2!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  // Active auto-refresh interval that keeps the list in sync while any row
  // is still queued or processing. Cleared in ngOnDestroy.
  private refreshIntervalHandle: any = null;

  // Cadence for the background refresh loop.
  private readonly refreshIntervalMs: number = 5000;

  constructor(
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    private backgroundExports: BackgroundExportService
  ) {
    this.listDataSource = new BackgroundExportsDataSource(this.app, this.api);
  }

  ngOnInit(): void {
    this.listDataSource.getData();
    // Keep the list fresh while any row is still in-flight so users see
    // the transition from queued -> processing -> completed without a manual
    // refresh. The interval only re-issues the request when there is at
    // least one non-terminal row in the current page.
    this.refreshIntervalHandle = setInterval(() => {
      this.refreshIfInflight();
    }, this.refreshIntervalMs);
  }

  ngAfterViewInit(): void {
    // Reset paginators when sorting changes.
    this.sort.sortChange.subscribe(() => {
      this.paginator1.pageIndex = 0;
      this.paginator2.pageIndex = 0;
    });

    const paginatorTap = tap((event: any) => {
      this.paginator1.pageIndex = event.pageIndex;
      this.paginator1.pageSize = event.pageSize;
      this.paginator2.pageIndex = event.pageIndex;
      this.paginator2.pageSize = event.pageSize;
      this.listDataSource.limit = event.pageSize;
      this.listDataSource.offset = event.pageIndex;
      this.listDataSource.getData();
    });
    this.paginator1.page.pipe(paginatorTap).subscribe();
    this.paginator2.page.pipe(paginatorTap).subscribe();

    this.sort.sortChange.pipe(tap((sorter: any) => {
      this.listDataSource.order = sorter.direction || 'desc';
      this.listDataSource.order_by = sorter.active || 'date_created';
      this.listDataSource.getData(true);
    })).subscribe();
  }

  ngOnDestroy(): void {
    if ( this.refreshIntervalHandle ) {
      clearInterval(this.refreshIntervalHandle);
      this.refreshIntervalHandle = null;
    }
  }

  /**
   * Trigger a browser download for a completed export. The row already
   * carries a short-lived presigned URL served by the index endpoint, so no
   * second round trip is required.
   */
  onDownload(row: BackgroundExportRow): void {
    if ( !row.file_url ) {
      this.utils.showToast('This export is not available to download yet.');
      return;
    }
    const anchor: HTMLAnchorElement = document.createElement('a');
    anchor.href = row.file_url;
    if ( row.file_name ) {
      anchor.download = row.file_name;
    }
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }

  /**
   * Show the sanitised error message for a failed export in a toast so the
   * user can decide whether to retry from the originating feature.
   */
  onShowFailureReason(row: BackgroundExportRow): void {
    const fallback: string = 'No further details were recorded.';
    const message: string = row.error_message && row.error_message.trim() !== ''
      ? row.error_message.trim().replace(/[\r\n]+/g, ' ')
      : fallback;
    this.utils.showModal('Export Failed', message);
  }

  /**
   * Open the filter dialog. Mirrors the pattern used by UserMessagesComponent.
   * Applies the returned selections to the data source and refreshes the list.
   */
  onFilter(): void {
    this.utils.showComponentDialog(
      BackgroundExportsFilterComponent,
      {
        filter_feature: this.listDataSource.feature,
        filter_format: this.listDataSource.format,
        filter_status: this.listDataSource.status,
        date_filter_range: this.listDataSource.date_filter_range
      },
      { width: '768px' },
      (results: any) => {
        if ( typeof results === 'undefined' ) {
          return;
        }
        this.listDataSource.feature = typeof results.filter_feature !== 'undefined' ? results.filter_feature : this.listDataSource.feature;
        this.listDataSource.format = typeof results.filter_format !== 'undefined' ? results.filter_format : this.listDataSource.format;
        this.listDataSource.status = typeof results.filter_status !== 'undefined' ? results.filter_status : this.listDataSource.status;
        this.listDataSource.date_filter_range = results.date_filter_range ? results.date_filter_range : this.listDataSource.date_filter_range;
        this.listDataSource.getData(true);
      }
    );
  }

  /**
   * Re-poll the list when any currently-visible row is still queued or
   * processing. Avoids polling when everything is already terminal.
   */
  private refreshIfInflight(): void {
    this.listDataSource.dataSubject.value.some((row: BackgroundExportRow) => {
      return row.status === 'queued' || row.status === 'processing';
    }) && this.listDataSource.getData();
  }
}

/**
 * Data source for the "My Exports" list. Calls the generic
 * `GET v2/background-exports` endpoint scoped to the authenticated user.
 */
export class BackgroundExportsDataSource extends ApiDataSource {
  override order_by = 'date_created';
  override order = 'desc';

  feature: string = '';
  format: string = '';
  status: string = '';
  date_filter_range: Date[] = [];

  getData(resetOffset: boolean = false) {
    return this.makeRequest('v2/background-exports', resetOffset, {
      feature: this.feature || '',
      format: this.format || '',
      status: this.status || '',
      date_filter_range: this.date_filter_range && this.date_filter_range.length > 1 ? [
        this.date_filter_range[0].getTime() / 1000,
        this.date_filter_range[1].getTime() / 1000
      ].join(',') : ''
    });
  }
}
